<template>
  <div class="container">
    <div class="title-block">
      {{ $t('LICENSE AGREEMENT') }}
    </div>
    <div
      class="flex justify-between"
      style="scroll-behavior: smooth;"
    >
      <div class="main-info">
        <div class="paragraph-block">
          <ol class="main-ol">
            <li
              ref="general"
              class="paragraph-title"
            >
              {{ $t('GENERAL') }}
              <ol class="paragraph-element">
                <li>{{ $t('This License Agreement governs the relationship between DreamFlat, the linensor(hereinafter also the “Licensor”), and you, the licensee of the Software(hereinafter also the “Licensee” or “User”), jointly referred to as the “Parties” and individually as a “Party”, in relation to the Platform.') }}</li>
                <li>
                  {{ $t('This Agreement is incorporated by reference into the DreamFlat ') }}
                  <ui-link
                    to="/terms-of-use"
                    class="link steps-link"
                    color="primary"
                  >
                    {{ $t('Terms of Use') }}
                  </ui-link>.
                  {{ $t(' Capitalized terms not defined here have the same meaning as those defined in the ') }}
                  <ui-link
                    to="/terms-of-use"
                    class="link steps-link"
                    color="primary"
                  >
                    {{ $t('Terms of Use') }}
                  </ui-link>.
                </li>
                <li>{{ $t('Before using the Software, the Licensee should read this Agreement alongside, and in addition to, our Platform terms and conditions (Terms of Use), our Privacy Policy and our Cookie Policy.') }}</li>
                <li>{{ $t('The Software does not collect any personal data. For information about how we collect, use or otherwise process information about you or your use of the Platform, please refer to our Privacy Policy.') }}</li>
              </ol>
            </li>
            <li
              ref="use-of-license"
              class="paragraph-title"
            >
              {{ $t('USE OF LICENSE') }}
              <ol class="paragraph-element">
                <li>Subject to your compliance with this Agreement, DreamFlat grants you a limited, non-exclusive, non-assignable, non-transferable, revocable license to install and use the Software on the terms provided herein (hereinafter the “License”).</li>
                <li>You may use the Software in the country or authorized territory where you purchased the Software, unless otherwise specified by the DreamFlat or applicable law. If you are an Individual User, you may use the Software in the territory in which you currently reside or are temporarily present. If the License is granted to a Business User, the Software may be used in the territory or region where the company is located, and always on condition that you have purchased a License from us.</li>
                <li>
                  Unless your subscription has expired or this License Agreement is terminated pursuant to the terms set forth in this License Agreement, you may use the Software as follows:
                  <ol>
                    <li>Install and use any version of the Software covered by the License on any operating system supported by the Software; and</li>
                    <li>Make one copy of the Software solely for archival, security and/or backup purposes.</li>
                  </ol>
                </li>
                <li>
                  Neither this License Agreement nor the presence of any information, data or other content in the
                  Software shall be construed to grant you a License to any other copyrighted material owned by
                  DreamFlat or to any DreamFlat trademark, service mark, logo, trade name, trade dress or
                  slogan.
                </li>
                <li>
                  You may use the Software as expressly permitted in the <ui-link
                    to="/terms-of-use"
                    class="link steps-link"
                    color="primary"
                  >
                    {{ $t('Terms of Use') }}
                  </ui-link> and this License Agreement.
                </li>
              </ol>
            </li>
            <li
              ref="limitation-and-registration"
              class="paragraph-title"
            >
              {{ $t('LIMITATIONS AND RESTRICTIONS') }}
              <ol class="paragraph-element">
                <li>
                  When using the Software you are not allowed to:
                  <ol>
                    <li>Allow more than one User to use the same subscription at the same time, unless the Software is provided to you under a Business User License as set forth in Section 4;</li>
                    <li>Make copies or otherwise reproduce the Software, except for installing it on Licensee's devices and backing it up, as specified above;</li>
                    <li>Use the Software to develop a new product with the same or similar primary function;</li>
                    <li>Redistribute or transfer the Software, in whole or in part, in its original or modified form, by any means (rent, lease, loan, fee, donation, grant, exchange, share or other) to any third party;</li>
                    <li>Create any derivative works, make any translation, adaptation, arrangement or any other modification of the Software, and make any reproduction, distribution, transfer, display or public presentation of the results of such activities;</li>
                    <li>Use the Software in a manner that infringes the copyrights or any other rights of third parties;</li>
                    <li>Reverse engineer, decompile, disassemble, modify, translate, or make any attempt to discover the source code of the Software;</li>
                    <li>Remove or obscure any proprietary or other notices contained in the Software.</li>
                  </ol>
                </li>
                <li>
                  You warrant that the Software is used only by you personally, if you are an Individual User, or by your employees, if you are a Business User, and you are responsible for your employees' compliance with this License Agreement. You must promptly notify us if you become aware of any unauthorized use of all or part of the Software by a third party. Notification must be made to <a
                    class="link q-mr-sm"
                    href="mailto:support@dreamflat.com"
                  >support@dreamflat.com</a>.
                </li>
              </ol>
            </li>
            <li
              ref="license-types"
              class="paragraph-title"
            >
              {{ $t('LICENSE TYPES') }}
              <ol class="paragraph-element">
                <li>
                  Depending on the number of Users using the Software, there are the Individual User License
                  and the Business User License, which are as follows:
                  <ol>
                    <li>
                      <b>Individual User License</b><br>
                      As part of the Individual User License, an Individual User can install the Software on up
                      to two devices, but it can only be used by one User at a time on one device. The
                      Individual User License is for a perpetual term, except as otherwise provided herein.
                    </li>
                    <li>
                      <b>Business User License</b><br>
                      A License for a Business User allows the Software to be installed on multiple devices
                      for use by any user in the company. The number of simultaneous Users cannot exceed
                      the number of licensed seats. Licensee may install initial copies of the Software as
                      specified on the permitted number of devices, solely by Licensee&#39;s employees or/and
                      contractors, and solely for Licensee&#39;s internal business needs.
                    </li>
                  </ol>
                </li>
                <li>
                  Depending on the set of Services there are the Start Services License and the Advanced
                  Service license, which are as follows:
                  <ol>
                    <li>The Start Services License offers a basic set of features and can be used free of charge;</li>
                    <li>
                      The Advanced Services License offers exclusive features and enhanced
                      functionality, such as “Premium”, “Designer+” and “Enterprise” and may be available
                      for purchase. You can find the terms and conditions of each Advanced Services
                      package on the Website in the &quot;Tariffs&quot; section.
                    </li>
                  </ol>
                </li>
                <li>
                  You can change or cancel Advanced Services any time. To change or cancel your subscription,
                  follow the instructions in your account on the Website. In case of cancellation, your subscription
                  will remain active from the time you cancel until the end of your current billing period, and you
                  will not receive a refund or credit for any remaining days in your current billing period. At the end
                  of your subscription you will lose access to all features associated with the Advanced Services
                  License.
                </li>
              </ol>
            </li>
            <li
              ref="payment"
              class="paragraph-title"
            >
              {{ $t('PAYMENT') }}
              <ol class="paragraph-element">
                <li>
                  By purchasing Advanced Services License, you agree to allow recurring monthly payments to
                  be made by DreamFlat using the payment method of your choice until the subscription is
                  cancelled by you or by DreamFlat.
                </li>
                <li>
                  Users in some regions may be required to pay local taxes, which will be calculated and added
                  to the total purchase price. Payment providers may apply their own fees and conversion rates to
                  payments in different currencies. This is not within our control.
                </li>
              </ol>
            </li>
            <li
              ref="intellectual"
              class="paragraph-title"
            >
              {{ $t('INTELLECTUAL PROPERTY AND OWNERSHIP') }}
              <ol class="paragraph-element">
                <li>
                  The Software, including without limitation all copyrights, patents, trademarks, trade
                  secrets, data, information, images, 3-D models, graphics, text and designs, layout and
                  design of the Software, music, audio, scripts, software and other intellectual property, is
                  protected by applicable intellectual property or other laws, including but not limited to
                  copyright protection, belong to DreamFlat at all times or to DreamFlat’s affiliates.
                </li>
                <li>
                  The User acknowledges that no intellectual property rights are transferred to the User
                  under this License Agreement, regardless of the use of terms such as &quot;purchase&quot; or
                  &quot;subscription. The DreamFlat has and retains all right, title and interest, including all
                  intellectual property rights, in and to the Software, any and all related or underlying
                  technology, and any modifications or derivative works of the Software.
                </li>
                <li>
                  The DreamFlat name, logo and other trademarks and illustrations used in connection
                  with the Software belong exclusively to the DreamFlat. The User has no rights to use
                  the above-mentioned intellectual property. The use of the abovementioned marks,
                  whether with the ® or ™ symbol, is expressly prohibited.
                </li>
              </ol>
            </li>
            <li
              ref="third-party-content"
              class="paragraph-title"
            >
              {{ $t('THIRD-PARTY CONTENT') }}
              <ol class="paragraph-element">
                <li>
                  The Software may include third-party content (including but not limited to data, information, 3D-
                  models or other product services) licensed to us by third parties. All third-party Software is
                  provided to you in accordance with the respective terms stipulated in the special documentation.
                </li>
              </ol>
            </li>
            <li
              ref="disclaimer"
              class="paragraph-title"
            >
              {{ $t('DISCLAIMER') }}
              <ol class="paragraph-element">
                <li>
                  THE SOFTWARE IS PROVIDED TO YOU “AS IS” AND “AS AVAILABLE&quot; AND WITH ALL
                  FAULTS AND DEFECTS WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT
                  PERMITTED UNDER APPLICABLE LAW, THE DREAMFLAT, ON ITS OWN BEHALF AND ON
                  BEHALF OF ITS AFFILIATES AND ITS AND THEIR RESPECTIVE LICENSORS AND
                  SERVICE PROVIDERS, EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER
                  EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, WITH RESPECT TO THE
                  APPLICATION, INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                  FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT, AND WARRANTIES
                  THAT MAY ARISE OUT OF COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE
                  OR TRADE PRACTICE.
                </li>
                <li>
                  WITHOUT LIMITATION TO THE FOREGOING, THE DREAMFLAT PROVIDES NO
                  WARRANTY OR UNDERTAKING, AND MAKES NO REPRESENTATION OF ANY KIND THAT
                  THE SOFTWARE WILL MEET YOUR REQUIREMENTS, ACHIEVE ANY INTENDED
                  RESULTS, BE COMPATIBLE OR WORK WITH ANY OTHER SOFTWARE, APPLICATIONS,
                  SYSTEMS OR SERVICES, OPERATE WITHOUT INTERRUPTION, MEET ANY
                  PERFORMANCE OR RELIABILITY STANDARDS OR BE ERROR FREE OR THAT ANY
                  ERRORS OR DEFECTS CAN OR WILL BE CORRECTED.
                </li>
                <li>
                  ANY CONTENT OR DATA DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE
                  USE OF THE SOFTWARE ARE DOWNLOADED AT USER’S OWN RISK; USER AGREES IT
                  IS SOLELY RESPONSIBLE FOR ANY DAMAGE TO ITS PROPERTY AND/OR LOSS OF
                  DATA THAT RESULTS FROM SUCH DOWNLOAD.
                </li>
                <li>
                  WITHOUT LIMITING THE FOREGOING, NEITHER THE DREAMFLAT NOR ANY OF THE
                  DREAMFLAT&#39;S PROVIDER MAKES ANY REPRESENTATION OR WARRANTY OF ANY
                  KIND, EXPRESS OR IMPLIED: (I) AS TO THE OPERATION OR AVAILABILITY OF THE
                  APPLICATION, OR THE INFORMATION, CONTENT, AND MATERIALS OR PRODUCTS
                  INCLUDED THEREON; (II) THAT THE SOFTWARE WILL BE UNINTERRUPTED OR ERROR-
                  FREE; (III) AS TO THE ACCURACY, RELIABILITY, OR CURRENCY OF ANY INFORMATION
                  OR CONTENT PROVIDED THROUGH THE SOFTWARE; OR (IV) THAT THE SOFTWARE,
                  ITS SERVERS, THE CONTENT, OR E-MAILS SENT FROM OR ON BEHALF OF THE
                  DREAMFLAT ARE FREE OF VIRUSES, SCRIPTS, TROJAN HORSES, WORMS, MALWARE,
                  TIMEBOMBS OR OTHER HARMFUL COMPONENTS.
                </li>
                <li>
                  SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN TYPES OF
                  WARRANTIES OR LIMITATIONS ON APPLICABLE STATUTORY RIGHTS OF A
                  CONSUMER, SO SOME OR ALL OF THE ABOVE EXCLUSIONS AND LIMITATIONS MAY
                  NOT APPLY TO YOU. BUT IN SUCH A CASE THE EXCLUSIONS AND LIMITATIONS SET
                  FORTH IN THIS SECTION SHALL BE APPLIED TO THE GREATEST EXTENT
                  ENFORCEABLE UNDER APPLICABLE LAW. TO THE EXTENT ANY WARRANTY EXISTS
                  UNDER LAW THAT CANNOT BE DISCLAIMED, THE DREAMFLAT SHALL BE SOLELY
                  RESPONSIBLE FOR SUCH WARRANTY.
                </li>
              </ol>
            </li>
            <li
              ref="limitation-of-liability"
              class="paragraph-title"
            >
              {{ $t('LIMITATION OF LIABILITY') }}
              <ol class="paragraph-element">
                <li>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL THE
                  DREAMFLAT BE LIABLE TO USER, USER’S AFFILIATES, OR ANYONE ELSE FOR: (A) ANY
                  LOSS OF USE, DATA, GOODWILL, OR PROFITS, WHETHER OR NOT FORESEEABLE; (B)
                  ANY LOSS OR DAMAGES IN CONNECTION WITH TERMINATION OR SUSPENSION OF
                  USER’S ACCESS TO THE SOFTWARE IN ACCORDANCE WITH THIS AGREEMENT; OR (C)
                  ANY SPECIAL, INCIDENTAL, INDIRECT, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE

                  DAMAGES WHATSOEVER (EVEN IF THE RELEVANT LISENSORS PARTY HAS BEEN
                  ADVISED OF THE POSSIBILITY OF THESE DAMAGES), INCLUDING THOSE (D)
                  RESULTING FROM LOSS OF USE, DATA, OR PROFITS, WHETHER OR NOT THEY ARE
                  FORESEEABLE, (E) BASED ON ANY THEORY OF LIABILITY, INCLUDING BREACH OF
                  CONTRACT OR WARRANTY, STRICT LIABILITY, NEGLIGENCE, OR OTHER TORTIOUS
                  ACTION, OR (F) ARISING FROM ANY OTHER CLAIM ARISING OUT OF OR IN
                  CONNECTION WITH YOUR USE OF OR ACCESS TO THE SOFTWARE OR SUPPORT. THIS
                  LIMITATION OF LIABILITY SHALL APPLY TO THE FULLEST EXTENT PERMITTED BY LAW
                  IN THE APPLICABLE JURISDICTION.
                </li>
                <li>
                  THE TOTAL LIABILITY OF THE DREAMFLAT IN ANY MATTER ARISING OUT OF OR IN
                  RELATION TO THIS LICENSE AGREEMENT IS LIMITED TO THE GREATER OF (A) ONE
                  HUNDRED (100) US DOLLARS OR (B) THE AGGREGATE AMOUNT PAID OR PAYABLE BY
                  USER DURING THE THREE-MONTH PERIOD PRECEDING THE EVENT FOR THE
                  SOFTWARE GIVING THE DREAMFLAT HAS BEEN ADVISED OF THE POSSIBILITY OF
                  LIABILITY EXCEEDING SUCH AN AMOUNT AND NOTWITHSTANDING ANY FAILURE OF
                  THE ESSENTIAL PURPOSE OF ANY LIMITED REMEDY.
                </li>
              </ol>
            </li>
            <li
              ref="termination"
              class="paragraph-title"
            >
              {{ $t('TERMINATION') }}
              <ol class="paragraph-element">
                <li>This License Agreement shall remain in effect until terminated by you or the DreamFlat.</li>
                <li>
                  The DreamFlat may, without prior notice or liability to you, suspend or terminate this License
                  Agreement, any rights granted herein, in our sole discretion, for any reason.
                </li>
                <li>
                  Unless you agree otherwise, you may terminate this License Agreement at any time by
                  terminating your access to and use of the Software. Upon the termination, all Licenses granted
                  herein will immediately terminate and you will cease using the Software.
                </li>
                <li>
                  Termination of this License Agreement will not limit any of the DreamFlat&#39;s rights or remedies at
                  law or in equity in case of breach by you (during the term of this License Agreement) of any of
                  your obligations hereunder.
                </li>
              </ol>
            </li>
            <li
              ref="closing-provisions"
              class="paragraph-title"
            >
              {{ $t('CLOSING PROVISIONS') }}
              <ol class="paragraph-element">
                <li>
                  The DreamFlat reserves the right to make changes and/or other updates to this
                  License Agreement at any time without notice. By continuing to access or use the
                  Software after any changes become effective, you agree to be bound by the revised
                  terms. We understand that you may not agree to the updated License Agreement. If
                  you do, you may terminate your subscription at any time within 30 days after the
                  effective date of the updated License Agreement. You will find the &quot;last updated&quot; date
                  at the end of these <ui-link
                    to="/terms-of-use"
                    class="link steps-link"
                    color="primary"
                  >
                    {{ $t('Terms of Use') }}
                  </ui-link>.
                </li>
                <li>
                  The DreamFlat reserves the right to discontinue support for the Software at any time
                  and to change the Software&#39;s prices, features, specifications, capabilities, functions,
                  terms of use, release dates, general availability and other specifications.
                </li>
                <li>
                  Neither Party shall breach this License Agreement or be otherwise liable to the other
                  Party by reason of any delay in the performance or non-performance of any of its
                  obligations under this License Agreement (other than payment obligations) arising
                  directly out of an act of God, fire, flood, natural disaster, act of terrorism, strike, lockout,
                  labor dispute, public health emergency, civil disturbance, riot or act of war.
                </li>
                <li>
                  The use of the Software shall be governed by the laws of the Emirate of Dubai and the
                  Federal laws of the UAE applicable thereto. In case any disputes or differences arise in
                  connection with the fulfillment of this License Agreement or are related to the use of the
                  Software, the Parties will make every effort to resolve these by negotiations. Those
                  disputes which are not resolved by negotiations shall be subject to the non-exclusive
                  jurisdiction of the Dubai courts.
                </li>
                <li>
                  If you have any questions or concerns about this License Agreement, please contact
                  us at <a
                    class="link q-mr-sm"
                    href="mailto:info@dreamflat.design"
                  >info@dreamflat.design</a>.
                </li>
              </ol>
            </li>
          </ol>
        </div>
      </div>
      <div class="legal-table-contents">
        <ol>
          <li><a @click="scrollToAnchorPoint('general')">{{ $t('GENERAL') }}</a></li>
          <li><a @click="scrollToAnchorPoint('use-of-license')">{{ $t('USE OF LICENSE') }}</a></li>
          <li><a @click="scrollToAnchorPoint('limitation-and-registration')">{{ $t('LIMITATIONS AND RESTRICTIONS') }}</a></li>
          <li><a @click="scrollToAnchorPoint('license-types')">{{ $t('LICENSE TYPES') }}</a></li>
          <li><a @click="scrollToAnchorPoint('payment')">{{ $t('PAYMENT') }}</a></li>
          <li><a @click="scrollToAnchorPoint('intellectual')">{{ $t('INTELLECTUAL PROPERTY AND OWNERSHIP') }}</a></li>
          <li><a @click="scrollToAnchorPoint('third-party-content')">{{ $t('THIRD-PARTY CONTENT') }}</a></li>
          <li><a @click="scrollToAnchorPoint('disclaimer')">{{ $t('DISCLAIMER') }}</a></li>
          <li><a @click="scrollToAnchorPoint('limitation-of-liability')">{{ $t('LIMITATION OF LIABILITY') }}</a></li>
          <li><a @click="scrollToAnchorPoint('termination')">{{ $t('TERMINATION') }}</a></li>
          <li><a @click="scrollToAnchorPoint('closing-provisions')">{{ $t('CLOSING PROVISIONS') }}</a></li>
        </ol>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {useI18n} from "vue-i18n";
import {useHead} from "#imports";

export default{
  setup () {
    const { t } = useI18n()
    useHead({
      title: `${t('aboutDf')}`,
      meta: [
        {
          name: 'description', content: `${t('licenseMetaDesc')}`,
        },
      ],
    })
    useSeoMeta({
      ogTitle: `${t('aboutDf')}`,
      description: `${t('licenseMetaDesc')}`,
      ogDescription: `${t('licenseMetaDesc')}`,
      ogImage: 'https://dreamflat.design/prev.webp',
    })
  },
  methods: {
    scrollToAnchorPoint(refName:string) {
      const el = (this as any).$refs[refName]
      el.scrollIntoView({ behavior: 'smooth'})
    },
  }
}

</script>
<style lang="scss">
.title-block{
  font-size: 36px;
  font-weight: 700;
  margin: 30px 0 60px 0;
  color: #3D424A;
  line-height: 120%;
  letter-spacing: 0.03em;
}
.main-info{
  width: 650px;
  .paragraph-block{
    color: #3D424A;
    ol {
      margin:0 0 1.5em;
      padding:0;
    }
    .main-ol{
      counter-reset:item;
    }
    ol>li {
      padding:0 0 0 1.5em;
      text-indent:-1.5em;
      list-style-type:none;
    }
    ol>li:before {
      display:inline-block;
      width:1.5em;
      padding-right:0.5em;
      font-weight:bold;
      text-align:right;
    }
    .main-ol>li{
      counter-increment:item;
    }
    .main-ol>li:before{
      content:counter(item) ".";
    }
    .main-ol>li ol>li{
      counter-increment:item-t;
    }
    .main-ol>li ol>li:before {
      content:counter(item) "." counter(item-t);
    }

    .main-ol>li ol>li ol>li{
      counter-increment:item-three;
      padding:0 0 0 3.1em;
      text-indent:-1.5em;
      margin: 16px 0;
    }

    .main-ol>li ol>li ol>li:before {
      display:inline-block;
      width:1.5em;
      padding-right:0.5em;
      font-weight:bold;
      text-align:right;
      content:counter(item) "." counter(item-t) "." counter(item-three);
    }


    a{
      color: $primary;
      text-decoration: none;
    }
    .paragraph-title{
      font-size: 24px;
      font-weight: 700;
      .paragraph-element>*{
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
        letter-spacing: 0.03em;
        margin: 16px 0;
      }
    }
  }
}
.steps-link:after{
  content: '';
}
.legal-table-contents{
  width: 265px;
  ol{
    color: #839AAF;
    padding: 0;
    margin:0 0 1.1em;
    position: sticky;
    top: 50px;
    li{
      list-style-position: inside;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.02em;
      margin-bottom: 8px;
      padding:0 0 0 1.1em;
      text-indent: -1.1em;
      cursor: pointer;
    }
    a{
      text-decoration: none;
      color: #839AAF;
    }
    a:active{
      color: #64686E;
    }
  }

}
@media (max-width: $lg) {
  .main-info{
    width: 90%;
  }
  .legal-table-contents{
    width: 90%;
  }
}
</style>
